import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

/**
 * ChangePasswordCard — self-serve password rotation for email/password users.
 *
 * Hits the existing `POST /api/auth/change-password` endpoint. Handles four states:
 *   1. Google-auth user (no password_hash)  → shows a Google-managed notice, no form.
 *   2. Idle form                            → current + new + confirm inputs.
 *   3. Submitting                           → button spins.
 *   4. Success                              → inline confirmation with a "change again" reset link.
 *
 * Client-side validation mirrors the server rules:
 *   - new_password ≥ 8 chars
 *   - new_password ≠ current_password
 *   - confirm_password must match new_password
 *
 * Server surfaces the "current password is incorrect" case (401) which we
 * route back to the current-password field for clarity.
 */
export default function ChangePasswordCard() {
    const { user } = useAuth();
    const googleOnly = user?.auth_provider === "google" && !user?.password_hash_present;

    const [current, setCurrent] = useState("");
    const [next,    setNext]    = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy,    setBusy]    = useState(false);
    const [err,     setErr]     = useState(null);
    const [ok,      setOk]      = useState(false);

    function reset() {
        setCurrent(""); setNext(""); setConfirm("");
        setErr(null); setOk(false);
    }

    async function submit(e) {
        e.preventDefault();
        setErr(null);

        if (next.length < 8) {
            setErr("New password must be at least 8 characters.");
            return;
        }
        if (next === current) {
            setErr("New password must differ from your current password.");
            return;
        }
        if (next !== confirm) {
            setErr("New password and confirmation don't match.");
            return;
        }

        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            setOk(true);
            setCurrent(""); setNext(""); setConfirm("");
            toast.success("Password updated. Use it next time you sign in.");
        } catch (e2) {
            const detail = formatApiErrorDetail(e2?.response?.data?.detail);
            setErr(detail || "Failed to update password. Please try again.");
        } finally {
            setBusy(false);
        }
    }

    // ── State 1: Google-only user — nothing to change here ──────────────
    if (googleOnly) {
        return (
            <div className="border border-border bg-card rounded p-4" data-testid="change-password-card">
                <div className="overline mb-3 flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5" /> Password
                </div>
                <div className="text-sm text-muted-foreground">
                    You signed up with <b className="text-foreground">Google</b>. Your password is managed by Google —
                    change it in your Google account settings.
                </div>
            </div>
        );
    }

    // ── State 4: Success ─────────────────────────────────────────────────
    if (ok) {
        return (
            <div className="border border-border bg-card rounded p-4" data-testid="change-password-card">
                <div className="overline mb-3 flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5" /> Password
                </div>
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold mb-1" data-testid="change-password-success">
                            Password updated.
                        </div>
                        <div className="text-xs text-muted-foreground mb-3">
                            Your recovery codes are unchanged and still valid.
                        </div>
                        <button
                            type="button"
                            className="text-xs text-primary hover:underline"
                            onClick={reset}
                            data-testid="change-password-reset"
                        >
                            Change it again →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── States 2 & 3: Form ───────────────────────────────────────────────
    return (
        <div className="border border-border bg-card rounded p-4" data-testid="change-password-card">
            <div className="overline mb-3 flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5" /> Password
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
                <div>
                    <Label htmlFor="cp-current" className="text-xs">Current password</Label>
                    <PasswordInput
                        id="cp-current"
                        required
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        testId="change-password-current"
                        toggleTestId="change-password-current-toggle"
                        autoComplete="current-password"
                    />
                </div>
                <div>
                    <Label htmlFor="cp-next" className="text-xs">New password</Label>
                    <PasswordInput
                        id="cp-next"
                        required
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        testId="change-password-new"
                        toggleTestId="change-password-new-toggle"
                        autoComplete="new-password"
                    />
                    <PasswordStrengthMeter
                        password={next}
                        disallow={[user?.email?.split("@")[0], user?.full_name].filter(Boolean)}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">Minimum 8 characters.</p>
                </div>
                <div>
                    <Label htmlFor="cp-confirm" className="text-xs">Confirm new password</Label>
                    <PasswordInput
                        id="cp-confirm"
                        required
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        testId="change-password-confirm"
                        toggleTestId="change-password-confirm-toggle"
                        autoComplete="new-password"
                    />
                </div>

                {err && (
                    <div className="text-danger text-xs" data-testid="change-password-error">
                        {err}
                    </div>
                )}

                <Button
                    type="submit"
                    className="w-full"
                    disabled={busy || !current || !next || !confirm}
                    data-testid="change-password-submit"
                >
                    {busy ? "Updating…" : "Update password"}
                </Button>

                <p className="text-[11px] text-muted-foreground">
                    Forgot it instead? Use one of your <b className="text-foreground">10 recovery codes</b> from the sign-in screen.
                </p>
            </form>
        </div>
    );
}
